import { useId, useState } from 'react';

import { useT } from '../i18n/provider';
import { cn } from '../lib/cn';

import { Button } from './Button';
import { fieldStyles } from './styles';
import { Modal } from './Modal';

/**
 * El diálogo de acción destructiva que pide `13 §6`: *«acción destructiva con
 * diálogo que exige escribir el nombre del recurso»*, nombrado allí en concreto
 * para archivar una empresa.
 *
 * Escribir el nombre no es fricción por la fricción: es la única confirmación
 * que un usuario no puede dar por reflejo, que es justo lo que un «¿estás
 * seguro?» no consigue evitar.
 *
 * Se apoya en `Modal` para el fondo, el foco y el Escape, y se queda solo con
 * lo suyo: la comparación y el botón que no se enciende hasta que cuadra.
 */
export function ConfirmByName({
  name,
  verb,
  description,
  busy = false,
  onConfirm,
  onCancel,
}: {
  name: string;
  verb: string;
  description: string;
  busy?: boolean | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const matches = typed.trim() === name;
  const touched = typed.trim().length > 0;

  return (
    <Modal
      open
      onClose={onCancel}
      size="sm"
      title={`${verb} ${name}`}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" disabled={!matches} busy={busy} onClick={onConfirm}>
            {verb}
          </Button>
        </>
      }
    >
      {/*
        La frase se parte por el marcador en lugar de interpolarse: el nombre va
        en monoespaciada porque es lo que hay que copiar carácter a carácter, y
        eso no cabe dentro de una cadena. El orden de las dos mitades lo decide
        la traducción, que es justo lo que se perdería concatenando a mano.
      */}
      <label htmlFor={inputId} className="block text-sm text-ink">
        {t('confirm.typeToConfirm')
          .split('{name}')
          .flatMap((part, index) => [
            part,
            index === 0 ? (
              <span key="name" className="font-mono font-semibold text-ink">
                {name}
              </span>
            ) : null,
          ])}
      </label>

      <input
        id={inputId}
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        autoComplete="off"
        aria-invalid={touched && !matches}
        className={cn(fieldStyles, 'mt-2', touched && !matches ? 'border-danger' : 'border-line')}
      />

      {touched && !matches ? (
        <p className="animate-rise mt-1.5 text-xs font-semibold text-danger">{t('confirm.nameDoesNotMatch')}</p>
      ) : null}
    </Modal>
  );
}
